import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hoop-tracker-7/artifacts/tisywt9m_attendance%20tracker%20logo.png';

const blogPosts = [
  {
    id: 'free-athlete-attendance-tracking-app',
    title: 'Free Athlete Attendance Tracking App for Coaches in 2025',
    excerpt: 'Discover how coaches can track athlete attendance for free with modern digital tools. No more paper sign-in sheets or spreadsheets.',
    category: 'Guide',
    readTime: '5 min read',
    icon: <Users className="h-6 w-6" />,
    date: 'March 2025'
  },
  {
    id: 'how-to-track-sports-team-attendance',
    title: 'How to Track Sports Team Attendance Effectively',
    excerpt: 'Learn the best practices for tracking attendance in youth sports, club teams, and athletic programs.',
    category: 'Best Practices',
    readTime: '4 min read',
    icon: <CheckCircle className="h-6 w-6" />,
    date: 'March 2025'
  },
  {
    id: 'best-attendance-tracking-youth-sports',
    title: 'Best Attendance Tracking Solutions for Youth Sports',
    excerpt: 'Compare the top attendance tracking methods for youth sports programs and find the right solution for your team.',
    category: 'Comparison',
    readTime: '6 min read',
    icon: <Calendar className="h-6 w-6" />,
    date: 'March 2025'
  },
  {
    id: 'why-coaches-need-attendance-tracking',
    title: 'Why Every Coach Needs an Attendance Tracking System',
    excerpt: 'From improving team accountability to satisfying insurance requirements, discover why attendance tracking is essential.',
    category: 'Insights',
    readTime: '4 min read',
    icon: <Clock className="h-6 w-6" />,
    date: 'March 2025'
  }
];

const BlogPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="Attendance Tracker" className="h-10 w-auto" />
              <span className="font-bold text-xl text-gray-900">Attendance Tracker</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost">Login</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Get Started Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-700">Resources</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
            Attendance Tracker Blog
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Tips, guides, and best practices for tracking athlete attendance and managing sports teams effectively.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.id}`}>
                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="outline" className="text-blue-600 border-blue-200">
                        {post.category}
                      </Badge>
                      <span className="text-sm text-gray-500">{post.readTime}</span>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                        {post.icon}
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-gray-600 mb-4">{post.excerpt}</p>
                        <span className="text-blue-600 font-medium flex items-center">
                          Read more <ArrowRight className="h-4 w-4 ml-1" />
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">
            Ready to simplify your attendance tracking?
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Join thousands of coaches using Attendance Tracker to manage their teams.
          </p>
          <div className="mt-8">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Start Free Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="text-white hover:text-gray-300">
            ← Back to Home
          </Link>
          <p className="mt-4">&copy; {new Date().getFullYear()} Attendance Tracker App. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export { blogPosts };
export default BlogPage;
